import { supabaseClient } from './supabase';
import { toast } from './toast';

interface QueueItem {
  id: string;
  analysis_id: string;
  priority: number;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  attempts: number;
  last_attempt_at: string | null;
  created_at: string;
  updated_at: string;
  error_message: string | null;
}

export async function addToQueue(analysisId: string, priority: number = 5): Promise<boolean> {
  try {
    // Check if analysis exists
    const { data: analysis, error: analysisError } = await supabaseClient
      .from('analyses')
      .select('id')
      .eq('id', analysisId)
      .single();

    if (analysisError) throw analysisError;
    if (!analysis) throw new Error('Analysis not found');

    // Check if already in queue
    const { data: existingItem, error: existingError } = await supabaseClient
      .from('analysis_queue')
      .select('id, status')
      .eq('analysis_id', analysisId)
      .maybeSingle();

    if (existingError) throw existingError;

    // If already in queue and not completed/failed, update priority
    if (existingItem && ['queued', 'processing'].includes(existingItem.status)) {
      const { error: updateError } = await supabaseClient
        .from('analysis_queue')
        .update({ priority })
        .eq('id', existingItem.id);

      if (updateError) throw updateError;
      return true;
    }

    // If completed/failed or not in queue, add new entry
    const { error: insertError } = await supabaseClient
      .from('analysis_queue')
      .insert({
        analysis_id: analysisId,
        priority,
        status: 'queued'
      });

    if (insertError) throw insertError;
    return true;
  } catch (error) {
    console.error('Error adding to queue:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to add to queue');
    return false;
  }
}

export async function removeFromQueue(queueItemId: string): Promise<boolean> {
  try {
    const { error } = await supabaseClient
      .from('analysis_queue')
      .delete()
      .eq('id', queueItemId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error removing from queue:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to remove from queue');
    return false;
  }
}

export async function changePriority(queueItemId: string, newPriority: number): Promise<boolean> {
  try {
    const { error } = await supabaseClient
      .from('analysis_queue')
      .update({ priority: newPriority })
      .eq('id', queueItemId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error changing priority:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to change priority');
    return false;
  }
}

export async function retryFailedItem(queueItemId: string): Promise<boolean> {
  try {
    const { error } = await supabaseClient
      .from('analysis_queue')
      .update({
        status: 'queued',
        error_message: null,
        updated_at: new Date().toISOString()
      })
      .eq('id', queueItemId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error retrying failed item:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to retry item');
    return false;
  }
}

export async function processQueue(): Promise<{
  processed: number;
  success: number;
  failed: number;
}> {
  try {
    const { data: { session }, error: sessionError } = await supabaseClient.auth.getSession();
    
    if (sessionError) {
      throw new Error(`Authentication error: ${sessionError.message}`);
    }
    
    if (!session) {
      throw new Error('No active session found');
    }

    // First, check for any stuck processing items (older than 10 minutes)
    const tenMinutesAgo = new Date();
    tenMinutesAgo.setMinutes(tenMinutesAgo.getMinutes() - 10);
    
    const { data: stuckItems, error: stuckError } = await supabaseClient
      .from('analysis_queue')
      .select('id')
      .eq('status', 'processing')
      .lt('last_attempt_at', tenMinutesAgo.toISOString());
    
    if (stuckError) {
      console.error('Error checking for stuck items:', stuckError);
    } else if (stuckItems && stuckItems.length > 0) {
      console.log(`Found ${stuckItems.length} stuck items, resetting to queued state`);
      
      // Reset stuck items to queued state
      for (const item of stuckItems) {
        await supabaseClient
          .from('analysis_queue')
          .update({
            status: 'queued',
            error_message: 'Reset from stuck processing state'
          })
          .eq('id', item.id);
      }
    }

    // Add timeout to the fetch request
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-analysis-queue`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          signal: controller.signal
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to parse error response' }));
        throw new Error(
          `Queue processing failed with status ${response.status}: ${
            errorData.error || response.statusText
          }`
        );
      }

      const result = await response.json().catch(() => {
        throw new Error('Failed to parse response from queue processor');
      });

      if (!result || typeof result.processed === 'undefined') {
        throw new Error('Invalid response format from queue processor');
      }

      return {
        processed: result.processed || 0,
        success: result.success || 0,
        failed: result.failed || 0
      };
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (fetchError.name === 'AbortError') {
        throw new Error('Queue processing request timed out after 60 seconds');
      }
      throw fetchError;
    }
  } catch (error) {
    console.error('Error processing queue:', error);
    
    // Provide more specific error messages based on the error type
    let errorMessage = 'Failed to process queue';
    if (error instanceof Error) {
      if (error.message.includes('No active session')) {
        errorMessage = 'Please log in to process the queue';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = 'Network error: Could not connect to queue processor';
      } else if (error.message.includes('timed out')) {
        errorMessage = 'Request timed out: The server took too long to respond';
      } else if (error.message.includes('status 546')) {
        errorMessage = 'Server error: The request was rejected (status 546)';
      } else {
        errorMessage = error.message;
      }
    }
    
    toast.error(errorMessage);
    throw error; // Re-throw to allow the component to handle the error
  }
}

// Function to add multiple analyses to queue
export async function addMultipleToQueue(analysisIds: string[], priority: number = 5): Promise<{
  success: number;
  failed: number;
}> {
  let successCount = 0;
  let failedCount = 0;

  for (const analysisId of analysisIds) {
    const result = await addToQueue(analysisId, priority);
    if (result) {
      successCount++;
    } else {
      failedCount++;
    }
  }

  return { success: successCount, failed: failedCount };
}

// Function to get queue statistics
export async function getQueueStats(): Promise<QueueStats> {
  try {
    const { data, error } = await supabaseClient
      .from('analysis_queue')
      .select('status');

    if (error) throw error;

    const items = data || [];
    return {
      total: items.length,
      queued: items.filter(item => item.status === 'queued').length,
      processing: items.filter(item => item.status === 'processing').length,
      completed: items.filter(item => item.status === 'completed').length,
      failed: items.filter(item => item.status === 'failed').length
    };
  } catch (error) {
    console.error('Error getting queue stats:', error);
    return {
      total: 0,
      queued: 0,
      processing: 0,
      completed: 0,
      failed: 0
    };
  }
}

interface QueueStats {
  total: number;
  queued: number;
  processing: number;
  completed: number;
  failed: number;
}